// The first sign-in without an authenticator: set one up before anything else.
//
// WHY THIS IS A GATE AND NOT A NOTICE (Gabe, 11 Oct; PR #202). Every console
// role now needs a proved second factor for every read: Admins, Editors and,
// since 149, Viewers. A session that proved none is a session the service
// refuses everywhere except enrolment and sign-out, so drawing the desk for it
// would be drawing a page of refusals. This screen is what such a session gets
// instead, whatever address it opened on.
//
// WHEN IT IS DRAWN IS THE SESSION'S `second_factor_proved`, AND ONLY `false`.
// That field is what this session proved, not what the account holds — but an
// account that holds a factor cannot open a session without proving it (the
// emailed code alone is refused for it), so a session that proved none is an
// account with none. `null` is a session stored by an older build, which never
// recorded the answer; it is not gated, because "you have no authenticator"
// said to somebody who used one this morning is the wrong sentence. App.tsx
// makes the decision; this file draws the screen.
//
// THE STEPS ARE SETTINGS › SIGN-IN SECURITY'S OWN (Enrol.tsx in its gate
// mode): one enrolment, one set of words for the secret, the code and the
// refusals, wherever somebody meets it. What the gate adds is the frame — the
// sign-in page's, without the desk — and a way out that is not an enrolment.
//
// THE SESSION IN HAND DOES NOT CHANGE WHEN THE FACTOR IS CONFIRMED. The service
// stamps a session with the factor it was opened with, so the last step is
// signing in again with the next code. Enrol's "Sign out and sign in again"
// does that here, and the address is kept so it need not be typed twice.

import type { ReactNode } from "react";
import { RehearsalMark } from "@nova/desk-ui/parts";
import { NovaWordmark } from "@nova/desk-ui/logos";
import { CONSOLE_MARK_PARTS, SERVER_SENTENCE } from "../build/thisBuild.js";
import type { ApiOptions } from "../api/client.js";
import type { Session, SessionToken } from "../session/store.js";
import { ThemeToggle } from "../theme/theme.js";
import { Enrol } from "./Enrol.js";

export type FirstFactorProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly session: Session;
  /** One owner for the session: a 401 here is handed up, as everywhere. */
  readonly onSessionEnded: (detail: string) => void;
  /** Sign out, and open the sign-in form on this address. */
  readonly onSignInAgain: () => void;
  /** Sign out, and open the sign-in form empty. */
  readonly onSignOut: () => void;
  readonly signingOut: boolean;
};

export function FirstFactor({
  api,
  token,
  session,
  onSessionEnded,
  onSignInAgain,
  onSignOut,
  signingOut,
}: FirstFactorProps): ReactNode {
  return (
    <div className="frame console-root console-desk">
      {CONSOLE_MARK_PARTS === null ? null : (
        <RehearsalMark
          label={CONSOLE_MARK_PARTS.label}
          sentence={CONSOLE_MARK_PARTS.sentence}
          server={CONSOLE_MARK_PARTS.server}
          className="console-mark"
        />
      )}

      <main className="gate console-page console-gate first-factor">
        <div className="console-gate-theme">
          <ThemeToggle />
        </div>

        <div className="first-factor-box">
          <span className="brand">
            <NovaWordmark />
          </span>

          <Enrol
            api={api}
            token={token}
            session={session}
            onSessionEnded={onSessionEnded}
            onSignOut={onSignInAgain}
            mode="gate"
          />

          <div className="gate-alt">
            <button type="button" className="btn-link" disabled={signingOut} onClick={onSignOut}>
              {signingOut ? "Signing out…" : "Sign out"}
            </button>
          </div>
        </div>
      </main>

      <footer className="foot console-footer gate-foot">
        <p>{SERVER_SENTENCE}</p>
      </footer>
    </div>
  );
}
